import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "bootstrap-4-react/lib/components";
import { useDispatch, useSelector } from "react-redux";
import { fetchDefaultMarginDefaults, updateDefaultMarginDefaults } from "../../store/actions";
import LoaderSpinner from "../common/Loader";
import { toast } from "react-toastify";
import AccordionList from "../common/AccordionList";
import MarginCarrierFilter from "./CarrierFilter";
import _ from "lodash";
import { convertToFixed } from "../../config/utils";

const DefaultMarginSettings = ({ onBack }) => {
    const dispatch = useDispatch();
    const userDetails = useSelector((state) => state.auth.user);
    const { defaultMarginConfig, defaultMarginConfigStatus } = useSelector((state) => state.MarginReducer);
    const [marginData, setMarginData] = useState([]);
    const [allMarginData, setAllMarginData] = useState([]);
    const [editable, setEditable] = useState(true);
    const [isFormEdited, setIsFormEdited] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [error, setError] = useState({});

    const [payloadData, setPayloadData] = useState({
        minMarginPercentage: 0,
        maxMarginPercentage: 0,
        minMarginFlat: 0,
        maxMarginFlat: 0,
        filterCarrierCompanyIds: [],
        filterCarrierServiceIds: [],
    });

    useEffect(() => {
        dispatch(fetchDefaultMarginDefaults());
    }, []);

    useEffect(() => {
        if (defaultMarginConfigStatus === 'pending') {
            setShowLoader(true);
        } else {
            setShowLoader(false);
        }
    }, [defaultMarginConfigStatus]);

    useEffect(() => {
        const entity = defaultMarginConfig?.Entity || (Array.isArray(defaultMarginConfig) ? defaultMarginConfig : null);
        if (entity) {
            const data = JSON.parse(JSON.stringify(entity));
            setAllMarginData(data);
            setMarginData(data);
        }
    }, [defaultMarginConfig]);

    useEffect(() => {
        if (!showFilter) {
            setError({});
        }
    }, [showFilter]);

    const computeCarrierStatus = (services) => {
        return services.some(s => s.isActive);
    };

    const handleStatusChange = (status, index, serviceIndex) => {
        const data = JSON.parse(JSON.stringify(marginData));
        data[index].services[serviceIndex].isActive = status;
        data[index].services[serviceIndex].isModified = true;
        data[index].isActive = computeCarrierStatus(data[index].services);
        setMarginData(data);
    };

    const handleCarrierStatusChange = (checked, index) => {
        const data = JSON.parse(JSON.stringify(marginData));
        data[index].isActive = checked;
        data[index].services = data[index].services.map(s => ({ ...s, isActive: checked, isModified: true }));
        setMarginData(data);
    };

    const handleMarginValue = (e, index, serviceIndex, isBlur = false) => {
        let { value, name } = e.target;
        const validDecimalNum = /^(\d+\.?\d{0,2}|\.\d{0,2}|\.)$|^$/;
        if (!isBlur && !validDecimalNum.test(value)) return;
        if (isBlur) {
            value = value === "" ? 0 : value;
        }
        const data = marginData.map((item, i) => {
            if (i !== index) return item;
            return {
                ...item,
                services: item.services.map((service, si) => {
                    if (si !== serviceIndex) return service;
                    return {
                        ...service,
                        [name]: isBlur ? convertToFixed(value, 2) : value,
                        isModified: true
                    };
                })
            };
        });
        setMarginData(data);
    };

    useEffect(() => {
        if (marginData.length) {
            const findModified = marginData.flatMap(item =>
                item.services.filter(service => service.isModified === true)
            );
            setIsFormEdited(findModified.length > 0);
        }
    }, [marginData]);

    const handleSave = async () => {
        const payload = {
            Updates: marginData.map(carrier => ({
                CarrierCompanyId: carrier.carrierCompanyId,
                IsActive: carrier.isActive,
                Services: carrier.services.map(s => ({
                    CarrierServiceId: s.carrierServiceId || s.id,
                    IsActive: s.isActive,
                    MarginFlat: Number(s.marginFlat) || 0,
                    MarginPercentage: Number(s.marginPercentage) || 0
                }))
            }))
        };
        setShowLoader(true);
        try {
            const response = await Promise.resolve(dispatch(updateDefaultMarginDefaults(payload)));
            if (response?.data?.Status === 200) {
                toast.success('Default margin settings saved successfully');
                setIsFormEdited(false);
                setEditable(false);
                dispatch(fetchDefaultMarginDefaults());
            } else {
                toast.error(response?.data?.Message?.AppStatusDescription || 'Failed to save default margin settings');
            }
        } catch {
            toast.error('An error occurred while saving default margin settings');
        }
        setShowLoader(false);
    };

    const handleFilterValidation = () => {
        let isValidated = true;
        let allErrors = {};
        if (Number(payloadData.minMarginPercentage) > Number(payloadData.maxMarginPercentage)) {
            isValidated = false;
            allErrors.minMarginPercentage = 'Min margin percent cannot be greater than max margin percent';
        }
        if (Number(payloadData.minMarginFlat) > Number(payloadData.maxMarginFlat)) {
            isValidated = false;
            allErrors.minMarginFlat = 'Min flat margin cannot be greater than max flat margin';
        }
        setError(allErrors);
        return isValidated;
    };

    const handleFilter = () => {
        if (handleFilterValidation()) {
            let filtered = JSON.parse(JSON.stringify(allMarginData));
            const carrierIds = _.map(payloadData.filterCarrierCompanyIds, "id");
            const serviceIds = _.map(payloadData.filterCarrierServiceIds, "id");
            if (carrierIds.length > 0) {
                filtered = filtered.filter(c => carrierIds.includes(c.carrierCompanyId));
            }
            if (serviceIds.length > 0) {
                filtered = filtered.map(c => ({
                    ...c,
                    services: c.services.filter(s => serviceIds.includes(s.carrierServiceId || s.id))
                })).filter(c => c.services.length > 0);
            }
            if (Number(payloadData.minMarginFlat) > 0 || Number(payloadData.maxMarginFlat) > 0) {
                filtered = filtered.map(c => ({
                    ...c,
                    services: c.services.filter(s => {
                        const flat = Number(s.marginFlat) || 0;
                        const min = Number(payloadData.minMarginFlat) || 0;
                        const max = Number(payloadData.maxMarginFlat) || 0;
                        return (min === 0 || flat >= min) && (max === 0 || flat <= max);
                    })
                })).filter(c => c.services.length > 0);
            }
            if (Number(payloadData.minMarginPercentage) > 0 || Number(payloadData.maxMarginPercentage) > 0) {
                filtered = filtered.map(c => ({
                    ...c,
                    services: c.services.filter(s => {
                        const pct = Number(s.marginPercentage) || 0;
                        const min = Number(payloadData.minMarginPercentage) || 0;
                        const max = Number(payloadData.maxMarginPercentage) || 0;
                        return (min === 0 || pct >= min) && (max === 0 || pct <= max);
                    })
                })).filter(c => c.services.length > 0);
            }
            setMarginData(filtered);
        }
    };

    const handleFilterValue = (value, type) => {
        setPayloadData({ ...payloadData, [type]: value });
    };

    const handleResetFilter = () => {
        setPayloadData({
            minMarginPercentage: 0,
            maxMarginPercentage: 0,
            minMarginFlat: 0,
            maxMarginFlat: 0,
            filterCarrierCompanyIds: [],
            filterCarrierServiceIds: [],
        });
        setError({});
        setMarginData(JSON.parse(JSON.stringify(allMarginData)));
    };

    return (
        <div>
            <div className="main__tile_heading cus-btn-include-sec">
                <div>Default Margin Settings</div>
                <div className="top_right_button_container">
                    <Button
                        className="cus-seconday-bg-btn top-btn"
                        onClick={() => setShowFilter(!showFilter)}
                    >
                        <i className="fa fa-filter" aria-hidden="true"></i> Filter
                    </Button>
                    <Button
                        className="cus-seconday-bg-btn top-btn editButton"
                        onClick={() => setEditable(true)}
                        disabled={editable}
                    >
                        Edit
                    </Button>
                    <button
                        className="btn cus-seconday-bg-btn top-btn saveBtn"
                        onClick={handleSave}
                        disabled={!isFormEdited}
                    >
                        Save
                    </button>
                    {onBack && (
                        <Button
                            className="cus-primary-transparent-btn"
                            onClick={onBack}
                        >
                            Back
                        </Button>
                    )}
                </div>
            </div>

            <div className="mb-3 pl-3">
                <div className="alert alert-info" role="alert" style={{ fontSize: '13px' }}>
                    <i className="fa fa-info-circle me-2 pr-1" aria-hidden="true"></i>These settings apply only to newly created seller accounts. Existing seller configurations are not affected.
                </div>
            </div>

            {showLoader && <LoaderSpinner />}

            <MarginCarrierFilter
                showFilter={showFilter}
                userDetails={userDetails}
                handleResetFilter={handleResetFilter}
                handleFilter={handleFilter}
                error={error}
                payloadData={payloadData}
                setPayloadData={setPayloadData}
                handleFilterValue={handleFilterValue}
                isMarginFilter={true}
            />

            <div className="cus-page-content-sec">
                <div className="top_right_button_container cus-content-only-sec">
                    <div className="incoming_wrapper main_content_table incomingTbl marginConfigWrapper">
                        <div className='pl-3 pr-3'>
                            {defaultMarginConfigStatus === 'success' && marginData.length > 0 ? (
                                <AccordionList
                                    handleMarginValue={handleMarginValue}
                                    userDetails={userDetails}
                                    handleStatusChange={handleStatusChange}
                                    handleCarrierStatusChange={handleCarrierStatusChange}
                                    data={marginData}
                                    edit={editable}
                                    showCarrierToggle={true}
                                />
                            ) : null}
                            {marginData.length === 0 && defaultMarginConfigStatus === 'success' && (
                                <div className="alignCenter">No carriers configured</div>
                            )}
                            {defaultMarginConfigStatus === 'failure' && (
                                <div className="alignCenter text-danger">Failed to load default margin settings. Please try again later.</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

DefaultMarginSettings.propTypes = {
    onBack: PropTypes.func,
};

export default DefaultMarginSettings;
